import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

const YEAR = new Date().getFullYear();

const sections = [
  {
    title: 'Buscar imóveis',
    links: [
      { label: 'Apartamentos para alugar', to: '/?tipo=aluguel&categoria=apartamento' },
      { label: 'Casas para alugar', to: '/?tipo=aluguel&categoria=casa' },
      { label: 'Apartamentos à venda', to: '/?tipo=venda&categoria=apartamento' },
      { label: 'Casas à venda', to: '/?tipo=venda&categoria=casa' },
      { label: 'Terrenos à venda', to: '/?tipo=venda&categoria=terreno' },
      { label: 'Pontos comerciais', to: '/?categoria=ponto_comercial' },
    ],
  },
  {
    title: 'Anunciantes',
    links: [
      { label: 'Anunciar imóvel grátis', to: '/anunciar' },
      { label: 'Conheça o Premium', to: '/premium' },
      { label: 'Meus anúncios', to: '/meus-anuncios' },
      { label: 'Destacar anúncio', to: '/meus-anuncios' },
    ],
  },
  {
    title: 'Empresa',
    links: [
      { label: 'Como funciona', to: '/como-funciona' },
      { label: 'Termos de uso', to: '/termos' },
      { label: 'Privacidade', to: '/privacidade' },
      { label: 'Contato', to: '/' },
    ],
  },
];

export function Footer() {
  return (
    <footer className="bg-[#111111] text-white">
      <div className="mx-auto max-w-6xl px-4 pt-14 pb-10 sm:px-6">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          <div className="flex flex-col gap-5">
            <Link
              to="/"
              className="w-fit rounded-lg font-display text-2xl font-semibold tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              imóveis<span className="text-brand">já</span>
            </Link>

            <p className="max-w-[220px] text-sm leading-relaxed text-[#999999]">
              Aluguel e venda direto com o proprietário. Sem intermediários, sem taxa de anúncio.
            </p>

            <div className="mt-1 flex items-center gap-3">
              <SocialLink label="Instagram" href="#">
                <InstagramIcon />
              </SocialLink>
              <SocialLink label="Facebook" href="#">
                <FacebookIcon />
              </SocialLink>
              <SocialLink label="WhatsApp" href="#">
                <WhatsAppIcon />
              </SocialLink>
            </div>
          </div>

          {sections.map(section => (
            <div key={section.title} className="hidden sm:flex flex-col gap-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-[#666666]">
                {section.title}
              </h3>
              <ul className="flex flex-col gap-2.5">
                {section.links.map(link => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-[#AAAAAA] transition-colors duration-150 hover:text-white focus-visible:underline focus-visible:outline-none"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 hidden sm:grid grid-cols-1 gap-4 border-t border-white/10 pt-8 sm:mt-12 sm:grid-cols-3">
          <Perk icon={<ShieldIcon />} title="100% gratuito" desc="Publique seu imóvel sem pagar nada" />
          <Perk icon={<PhoneIcon />} title="Contato direto" desc="Fale com o proprietário via WhatsApp" />
          <Perk icon={<StarIcon />} title="Conheça o Premium" desc="Mais anúncios, mais visibilidade e desconto no destaque" />
        </div>
      </div>

      <div className="bg-[#0A0A0A]">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-[#555555] sm:flex-row sm:px-6">
          <span>© {YEAR} ImóveisJá. Todos os direitos reservados.</span>
          <div className="flex items-center gap-4">
            <Link to="/termos" className="transition-colors hover:text-[#999999]">Termos de uso</Link>
            <span aria-hidden>·</span>
            <Link to="/privacidade" className="transition-colors hover:text-[#999999]">Privacidade</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function SocialLink({ label, href, children }: { label: string; href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-[#AAAAAA] transition-colors duration-150 hover:border-white/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {children}
    </a>
  );
}

function Perk({ icon, title, desc }: { icon: ReactNode; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand/15 text-brand">
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold text-white">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-[#888888]">{desc}</p>
      </div>
    </div>
  );
}

function InstagramIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.64A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
